import { QuoteBody, QuoteRequest } from 'pages/quotes/src/store/interfaces'
import { BaseServiceProvider } from './BaseServiceProvider'
import {
  QuoteResponse,
  QuotesFilters,
  QuotesHistoryResponse,
  QuotesParams,
} from '../api/quote/interface'
import {
  QuoteActionType,
  trackUserAction,
} from 'src/config/analytics/UserActionsManager'
import {
  getSavedVehicleRegion,
  getVehicleRegionId,
} from 'libs/cart/src/helpers/vehicleDataStorage'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { IS_CCL_ENABLED } from 'src/config/ConfigManager'

class QuotesServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'quote', '1.2', '')
  }

  saveAsQuote = async (body: QuoteRequest[]): Promise<QuoteResponse[]> => {
    try {
      // For multi-vehicle quotes, we need to handle each quote's regionId individually
      // If all quotes have the same regionId, use it as a parameter
      // Otherwise, let the API handle individual vehicle regions
      const regionIds = body
        .map((quoteReq) =>
          getVehicleRegionId(
            quoteReq.quote.vehicle,
            StoreInstances?.searchStore?.cclVehiclesData,
            StoreInstances?.userStore?.regionId
          )
        )
        .filter((id) => id !== undefined)

      const uniqueRegionIds = [...new Set(regionIds)]
      const singleRegionId =
        uniqueRegionIds.length === 1 ? uniqueRegionIds[0] : undefined

      const resp = await this.post<QuoteResponse[]>('quote', body, {
        params: singleRegionId ? { regionId: singleRegionId } : undefined,
      })
      return resp.data
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  editQuote = async (body: QuoteBody): Promise<QuoteResponse[]> => {
    try {
      const regionId = getVehicleRegionId(
        body.vehicle,
        StoreInstances?.searchStore?.cclVehiclesData,
        StoreInstances?.userStore?.regionId
      )
      const resp = await this.put<QuoteResponse[]>('quote', body, {
        params: regionId ? { regionId } : undefined,
      })
      return resp.data
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  deleteQuote = async (quoteId?: number): Promise<void> => {
    try {
      if (quoteId) {
        // track quote delete for lost sales
        await trackUserAction(
          QuoteActionType.quoteDelete,
          [],
          quoteId.toString()
        )
      }
      const regionId =
        getSavedVehicleRegion() ??
        (IS_CCL_ENABLED ? StoreInstances?.userStore?.regionId : undefined)
      await this.delete(`cart/${quoteId}`, {
        params: regionId ? { regionId } : undefined,
      })
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  deleteQuoteByTime = async (months?: number): Promise<void> => {
    try {
      const regionId =
        getSavedVehicleRegion() ??
        (IS_CCL_ENABLED ? StoreInstances?.userStore?.regionId : undefined)
      await this.delete(`quote`, {
        params: {
          months: months && months.toString(),
          regionId,
        },
      })
    } catch (error) {
      throw new Error(error.toString())
    }
  }

  getQuotesHistory = async (
    filters?: QuotesFilters,
    params?: QuotesParams,
    start?: number,
    limit?: number
  ): Promise<QuotesHistoryResponse> => {
    try {
      const regionId =
        getSavedVehicleRegion() ??
        (IS_CCL_ENABLED ? StoreInstances?.userStore?.regionId : undefined)
      const resp = await this.post<QuotesHistoryResponse>('search', filters, {
        params: {
          orgId: params.orgId,
          page: start,
          pageSize: limit,
          regionId,
        },
      })
      if (resp.status === 200) {
        return resp.data
      }
      if (resp.status === 204) {
        return {
          totalCount: 0,
          data: [],
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Bulk disabling. Fix if possible.
    } catch (error: any) {
      if (error?.message === 'Request failed with status code 400') {
        throw new Error('searchTermShouldBeMinimum3Characters')
      } else {
        throw new Error(error.toString())
      }
    }
    return {
      totalCount: 0,
      data: [],
    }
  }

  fetchQuoteById = async (quoteId: string): Promise<QuoteResponse> => {
    try {
      const regionId =
        getSavedVehicleRegion() ??
        (IS_CCL_ENABLED ? StoreInstances?.userStore?.regionId : undefined)
      const resp = await this.get<QuoteResponse>(`quote/${quoteId}`, {
        params: regionId ? { regionId } : undefined,
      })
      if (resp.status === 200) {
        resp.data.labors.forEach((labor) => {
          if (labor && labor.description) {
            labor['laborDescription'] = labor['description']
            delete labor['description']
          }
        })
        return resp.data
      }
    } catch (_e) {
      throw new Error('quoteNotFound')
    }
    return undefined
  }

  fetchQuoteByCartId = async (
    cartId: string | undefined
  ): Promise<QuoteResponse[]> => {
    try {
      const regionId =
        getSavedVehicleRegion() ??
        (IS_CCL_ENABLED ? StoreInstances?.userStore?.regionId : undefined)
      const resp = await this.get<QuoteResponse[]>(`cart/${cartId}`, {
        params: regionId ? { regionId } : undefined,
      })
      if (resp.status === 200) {
        resp.data.forEach((quote) => {
          if (quote.labors && Array.isArray(quote.labors)) {
            quote.labors.forEach((labor) => {
              if (labor && labor.description) {
                labor['laborDescription'] = labor['description']
                delete labor['description']
              }
            })
          }
        })
        return resp.data
      }
    } catch (_e) {
      throw new Error('quoteNotFound')
    }
    return undefined
  }
}

export default new QuotesServiceProvider()
